
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { queryMapFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, dataHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap, maxDate, minDate, parseDate } from "../../common/utils/dateUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = a => a.id;

const prepareArray = data => ensureArray(data).map(availability => ({
    ...availability,
    hours: ((parseDate(availability.end) - parseDate(availability.start)) / (1000 * 60 * 60)).toFixed(2)
}))

export const availabilityStaffSlice = createSlice({
    name: 'staffAvailability',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.ids.forEach(id => delete state.data[id]);
            prepareArray(action.payload.data).forEach(a => state.data[key(a)] = a);
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(a => state.data[key(a)] = a);
        },
        remove: (state, action) => {
            prepareArray(action.payload).forEach(a => delete state.data[key(a)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => availabilityStaffSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shiftAvailability', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shiftAvailability');

const fetchAvailability = aggregatingFetch(
    id => network => network.post('/api/v1/staffMemberAvailabilities', [id],  data => availabilityStaffSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v1/staffMemberAvailabilities', ids, data => availabilityStaffSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
);

const fetchMemberAvailabilitiesInRange = aggregatingFetch(
    (start, end) => network => network.get('/api/v2/staffMemberAvailabilities/' + encodeURIComponent(start) + '/' + encodeURIComponent(end), availabilityStaffSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.get('/api/v2/staffMemberAvailabilities/' + encodeURIComponent(minDate(keys, 0)) + '/' + encodeURIComponent(maxDate(keys, 1)), availabilityStaffSlice.actions.addOrUpdate, visualizeFailure('get'))
);


// #endregion

// #region selectors
const selectAvailabilityData = state => state.staffAvailability.data
const selectMemberAvailabilityInRange = (start, end) => createSelector([selectAvailabilityData], availabilities => Object.values(availabilities).filter(a => dateRangeOverlap(a, { start, end })));
// #endregion

const listen = {
    availabilityStaffCreated: (availability, { dispatch, websocket }) => {
        markFetched(websocket, 'availability', key(availability));
        dispatch(availabilityStaffSlice.actions.addOrUpdate(availability))
    },
    availabilityStaffUpdated: (availability, { dispatch }) => dispatch(availabilityStaffSlice.actions.addOrUpdate(availability)),
    availabilityStaffRemoved: (availability, { dispatch }) => dispatch(availabilityStaffSlice.actions.remove(availability))
}

const availabilityInitByID = initMapFactory(fetchAvailability, listen, { name: 'availabilityByID' });
registerEntityBasedFetch('availability', availabilityInitByID);

const options = {
    cleanup: { root: availabilityStaffSlice.name, all: state => Object.values(selectAvailabilityData(state)), removeAll: availabilityStaffSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('member') 
}
export const [useStaffMemberAvailabilityInRange] = dataHookWithCredentialsFactory(selectMemberAvailabilityInRange, queryMapFactory(fetchMemberAvailabilitiesInRange, 'id', availabilityInitByID, listen), options);

const reducer = { name: availabilityStaffSlice.name, reducer: availabilityStaffSlice.reducer };
export default reducer;